import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, Undo2, ArrowLeft, Type, Ruler, Palette, Settings2, Image as ImageIcon, FileText, Tag, Landmark, ShieldCheck, Download, Upload, Plus, Trash2, Check, X, Activity, Camera, MapPin, GripVertical, Wallet, Boxes, Search, TrendingUp, TrendingDown, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { CashflowTab, InventoryTab } from "@/pages/OperationsTabs";

const TABS_PUBLIC = [
  { id: "theme",       label: "Tampilan",   icon: Palette },
  { id: "content",     label: "Konten",     icon: FileText },
  { id: "earthquake",  label: "Gempa",      icon: Activity },
  { id: "gallery",     label: "Galeri",     icon: Camera },
  { id: "branding",    label: "Logo",       icon: ImageIcon },
  { id: "categories",  label: "Kategori",   icon: Tag },
  { id: "banks",       label: "Bank",       icon: Landmark },
  { id: "verify",      label: "Verifikasi", icon: ShieldCheck },
];
const TABS_OPS = [
  { id: "cashflow",    label: "Keuangan",   icon: Wallet },
  { id: "inventory",   label: "Inventaris", icon: Boxes },
  { id: "export",      label: "Ekspor",     icon: Download },
];
const TABS = [...TABS_PUBLIC, ...TABS_OPS];

function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{label}</label>
        {hint && <span className="text-[11px] font-mono text-[#141414]/50">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function Slider({ value, onChange, min, max, step = 1, testid }) {
  return <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} data-testid={testid} className="w-full accent-[color:var(--maroon)]" />;
}
function Select({ value, onChange, options, testid }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full flat-border rounded-lg px-3 py-2 outline-none bg-white text-sm">{options.map((o) => <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>)}</select>;
}
function Toggle({ value, onChange, testid }) {
  return <button type="button" onClick={() => onChange(!value)} data-testid={testid} className={`w-11 h-6 rounded-full flat-border relative transition-colors ${value ? "bg-[color:var(--maroon)]" : "bg-white"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white flat-border transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} /></button>;
}
function ColorInput({ value, onChange, testid }) {
  return <div className="flex items-center gap-2"><input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-picker`} className="w-10 h-10 flat-border rounded-lg cursor-pointer p-0" /><input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="flex-1 flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" /></div>;
}
function TextInput({ value, onChange, testid, placeholder }) {
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="w-full flat-border rounded-lg px-3 py-2 outline-none text-sm" />;
}
function TextArea({ value, onChange, testid, rows = 2 }) {
  return <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full flat-border rounded-lg px-3 py-2 outline-none text-sm" />;
}

/* ---------------- Theme tab ---------------- */
function ThemeTab({ local, set }) {
  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-center gap-2 mb-5"><Type className="w-4 h-4" strokeWidth={2} /><h2 className="font-display text-xl">Tipografi</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Font Judul"><Select value={local.heading_family} onChange={(v) => set({ heading_family: v })} testid="heading-family" options={["Caught","Instrument Serif","Space Grotesk","System"]} /></Field>
          <Field label="Font Isi"><Select value={local.body_family} onChange={(v) => set({ body_family: v })} testid="body-family" options={["Space Grotesk","Instrument Serif","Caught","System"]} /></Field>
          <Field label="Ukuran Judul" hint={`${local.heading_scale.toFixed(2)}x`}><Slider value={local.heading_scale} onChange={(v)=>set({heading_scale:v})} min={0.75} max={1.5} step={0.05} testid="heading-scale" /></Field>
          <Field label="Ukuran Teks" hint={`${local.body_size}px`}><Slider value={local.body_size} onChange={(v)=>set({body_size:Math.round(v)})} min={14} max={20} step={1} testid="body-size" /></Field>
          <Field label="Ketebalan Judul" hint={local.heading_weight}><Slider value={local.heading_weight} onChange={(v)=>set({heading_weight:Math.round(v/100)*100})} min={400} max={900} step={100} testid="heading-weight" /></Field>
          <Field label="Ketebalan Teks" hint={local.body_weight}><Slider value={local.body_weight} onChange={(v)=>set({body_weight:Math.round(v/100)*100})} min={300} max={700} step={100} testid="body-weight" /></Field>
          <Field label="Jarak Huruf" hint={`${local.letter_spacing.toFixed(3)}em`}><Slider value={local.letter_spacing} onChange={(v)=>set({letter_spacing:v})} min={-0.05} max={0.05} step={0.005} testid="letter-spacing" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Judul Italic"><Toggle value={local.heading_italic} onChange={(v)=>set({heading_italic:v})} testid="heading-italic" /></Field>
            <Field label="Judul Kapital"><Toggle value={local.heading_uppercase} onChange={(v)=>set({heading_uppercase:v})} testid="heading-upper" /></Field>
          </div>
        </div>
      </section>
      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-center gap-2 mb-5"><Ruler className="w-4 h-4" /><h2 className="font-display text-xl">Jarak & Bentuk</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Lebar Maksimum" hint={`${local.container_max}px`}><Slider value={local.container_max} onChange={(v)=>set({container_max:Math.round(v)})} min={960} max={1600} step={20} testid="container-max" /></Field>
          <Field label="Padding Section" hint={`${local.section_padding_y}px`}><Slider value={local.section_padding_y} onChange={(v)=>set({section_padding_y:Math.round(v)})} min={16} max={96} step={4} testid="section-padding" /></Field>
          <Field label="Padding Kartu" hint={`${local.card_padding}px`}><Slider value={local.card_padding} onChange={(v)=>set({card_padding:Math.round(v)})} min={12} max={48} step={2} testid="card-padding" /></Field>
          <Field label="Radius Sudut" hint={`${local.card_radius}px`}><Slider value={local.card_radius} onChange={(v)=>set({card_radius:Math.round(v)})} min={0} max={32} step={1} testid="card-radius" /></Field>
          <Field label="Ketebalan Garis" hint={`${local.border_width}px`}><Slider value={local.border_width} onChange={(v)=>set({border_width:v})} min={0.5} max={3} step={0.5} testid="border-width" /></Field>
        </div>
      </section>
      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-center gap-2 mb-5"><Palette className="w-4 h-4" /><h2 className="font-display text-xl">Warna</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Aksen (Primary)"><ColorInput value={local.color_primary} onChange={(v)=>set({color_primary:v})} testid="color-primary" /></Field>
          <Field label="Latar Belakang"><ColorInput value={local.color_background} onChange={(v)=>set({color_background:v})} testid="color-background" /></Field>
          <Field label="Teks / Garis"><ColorInput value={local.color_ink} onChange={(v)=>set({color_ink:v})} testid="color-ink" /></Field>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Content tab ---------------- */
function ContentTab({ local, set }) {
  const pairs = [
    ["Beranda · Judul", "hero_title_id", "hero_title_en"],
    ["Beranda · Subjudul", "hero_subtitle_id", "hero_subtitle_en"],
    ["Beranda · Label kecil (Live)", "hero_kicker_id", "hero_kicker_en"],
    ["Warga · Judul", "warga_title_id", "warga_title_en"],
    ["Warga · Subjudul", "warga_subtitle_id", "warga_subtitle_en"],
    ["Sumber Daya · Judul", "resources_title_id", "resources_title_en"],
    ["Sumber Daya · Subjudul", "resources_subtitle_id", "resources_subtitle_en"],
    ["Formulir · Judul", "submit_title_id", "submit_title_en"],
    ["Formulir · Subjudul", "submit_subtitle_id", "submit_subtitle_en"],
  ];
  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Teks Situs</h2>
        <p className="text-sm text-[#141414]/60 mb-5">Ubah judul dan deskripsi tiap halaman dalam dua bahasa. Tersimpan otomatis, format font mengikuti pengaturan Tampilan.</p>
        <div className="space-y-4">
          {pairs.map(([label, kid, ken]) => (
            <div key={kid}>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><div className="text-[10px] mb-1 opacity-60">Bahasa Indonesia</div>{kid.includes("subtitle") ? <TextArea value={local[kid]} onChange={(v)=>set({[kid]:v})} testid={`content-${kid}`} /> : <TextInput value={local[kid]} onChange={(v)=>set({[kid]:v})} testid={`content-${kid}`} />}</div>
                <div><div className="text-[10px] mb-1 opacity-60">English</div>{ken.includes("subtitle") ? <TextArea value={local[ken]} onChange={(v)=>set({[ken]:v})} testid={`content-${ken}`} /> : <TextInput value={local[ken]} onChange={(v)=>set({[ken]:v})} testid={`content-${ken}`} />}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Nama Menu Navigasi</h2>
        <p className="text-sm text-[#141414]/60 mb-5">Ubah nama halaman yang tampil di menu navigasi atas. Terapkan dua bahasa.</p>
        <div className="space-y-4">
          {[
            ["Beranda", "nav_dashboard_id", "nav_dashboard_en"],
            ["Warga", "nav_beneficiaries_id", "nav_beneficiaries_en"],
            ["Sumber Daya", "nav_resources_id", "nav_resources_en"],
            ["Dampak", "nav_impact_id", "nav_impact_en"],
            ["Formulir", "nav_submit_id", "nav_submit_en"],
          ].map(([label, kid, ken]) => (
            <div key={kid}>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><div className="text-[10px] mb-1 opacity-60">Bahasa Indonesia</div><TextInput value={local[kid]} onChange={(v)=>set({[kid]:v})} testid={`content-${kid}`} /></div>
                <div><div className="text-[10px] mb-1 opacity-60">English</div><TextInput value={local[ken]} onChange={(v)=>set({[ken]:v})} testid={`content-${ken}`} /></div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Judul Kartu di Beranda</h2>
        <p className="text-sm text-[#141414]/60 mb-5">Ubah judul tiga kartu ringkasan di halaman utama. Kosongkan untuk pakai teks bawaan.</p>
        <div className="space-y-4">
          {[
            ["Kartu Warga (People)", "snippet_people_title_id", "snippet_people_title_en"],
            ["Kartu Sumber Daya (Resources)", "snippet_resources_title_id", "snippet_resources_title_en"],
            ["Kartu Formulir (Submit)", "snippet_submit_title_id", "snippet_submit_title_en"],
          ].map(([label, kid, ken]) => (
            <div key={kid}>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><div className="text-[10px] mb-1 opacity-60">Bahasa Indonesia</div><TextInput value={local[kid]} onChange={(v)=>set({[kid]:v||null})} testid={`content-${kid}`} placeholder="Kosongkan untuk teks bawaan" /></div>
                <div><div className="text-[10px] mb-1 opacity-60">English</div><TextInput value={local[ken]} onChange={(v)=>set({[ken]:v||null})} testid={`content-${ken}`} placeholder="Leave empty for default" /></div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-4">Nama & Footer</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Judul Tab Browser"><TextInput value={local.site_title} onChange={(v)=>set({site_title:v})} testid="content-site-title" placeholder="Lepo Gete Flores" /></Field>
          <Field label="Nama Organisasi"><TextInput value={local.org_name} onChange={(v)=>set({org_name:v})} testid="content-org-name" /></Field>
          <Field label="Teks Footer"><TextInput value={local.footer_text} onChange={(v)=>set({footer_text:v})} testid="content-footer" /></Field>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Earthquake tab ---------------- */
function EarthquakeTab({ local, set }) {
  const clear = (k) => set({ [k]: null });
  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Tampilkan Bagian Gempa</h2>
        <p className="text-sm text-[#141414]/60 mb-4">Aktifkan atau matikan seluruh bagian peta & statistik gempa di halaman utama.</p>
        <div className="flex items-center gap-3">
          <Toggle value={local.eq_show_section !== false} onChange={(v)=>set({ eq_show_section: v })} testid="eq-show-toggle" />
          <span className="text-sm font-semibold">{local.eq_show_section === false ? "Tersembunyi" : "Ditampilkan"}</span>
        </div>
      </section>

      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Judul & Deskripsi</h2>
        <p className="text-sm text-[#141414]/60 mb-4">Kosongkan untuk pakai teks bawaan. Judul akan menggantikan "Peta Gempa Real-time".</p>
        <div className="space-y-4">
          {[
            ["Judul (ID / EN)", "eq_title_id", "eq_title_en"],
            ["Subjudul (ID / EN)", "eq_subtitle_id", "eq_subtitle_en"],
          ].map(([label, kid, ken]) => (
            <div key={kid}>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">{label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] mb-1 opacity-60">Bahasa Indonesia</div>
                  {kid.includes("subtitle") ? (
                    <TextArea value={local[kid]} onChange={(v)=>set({[kid]: v || null})} testid={`eq-${kid}`} />
                  ) : (
                    <TextInput value={local[kid]} onChange={(v)=>set({[kid]: v || null})} testid={`eq-${kid}`} placeholder="Kosongkan untuk teks bawaan" />
                  )}
                </div>
                <div>
                  <div className="text-[10px] mb-1 opacity-60">English</div>
                  {ken.includes("subtitle") ? (
                    <TextArea value={local[ken]} onChange={(v)=>set({[ken]: v || null})} testid={`eq-${ken}`} />
                  ) : (
                    <TextInput value={local[ken]} onChange={(v)=>set({[ken]: v || null})} testid={`eq-${ken}`} placeholder="Leave empty for default" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Timpa Manual (crosscheck BMKG)</h2>
        <p className="text-sm text-[#141414]/60 mb-4">
          Isi bila ingin menampilkan angka resmi dari rilis BMKG. Kosongkan (tekan <em>Reset</em>) untuk pakai deteksi otomatis.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Total Gempa Resmi</div>
            <div className="grid grid-cols-3 gap-2 items-start">
              <div className="col-span-2">
                <input type="number" min="0" value={local.bmkg_official_total ?? ""} onChange={(e)=>set({ bmkg_official_total: e.target.value === "" ? null : Number(e.target.value), bmkg_official_updated: new Date().toISOString().slice(0,10) })} data-testid="eq-bmkg-total" className="w-full flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" placeholder="mis. 3394" />
                <div className="text-[10px] text-[#141414]/60 mt-1">Diperbarui: {local.bmkg_official_updated || "—"}</div>
              </div>
              <button type="button" onClick={()=>{ clear("bmkg_official_total"); clear("bmkg_official_updated"); }} data-testid="eq-bmkg-total-reset" className="flat-btn bg-white rounded-lg px-2 py-2 text-[11px] font-semibold">Reset</button>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Gempa 24 Jam Terakhir</div>
            <div className="grid grid-cols-3 gap-2 items-start">
              <input type="number" min="0" value={local.eq_last_24h_override ?? ""} onChange={(e)=>set({ eq_last_24h_override: e.target.value === "" ? null : Number(e.target.value) })} data-testid="eq-last24-override" className="col-span-2 w-full flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" placeholder="kosong = otomatis" />
              <button type="button" onClick={()=>clear("eq_last_24h_override")} data-testid="eq-last24-reset" className="flat-btn bg-white rounded-lg px-2 py-2 text-[11px] font-semibold">Reset</button>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Magnitudo Terbesar</div>
            <div className="grid grid-cols-3 gap-2 items-start">
              <input type="number" step="0.1" min="0" value={local.eq_biggest_mag ?? ""} onChange={(e)=>set({ eq_biggest_mag: e.target.value === "" ? null : Number(e.target.value) })} data-testid="eq-biggest-mag" className="w-full flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" placeholder="mis. 7.7" />
              <input value={local.eq_biggest_place || ""} onChange={(e)=>set({ eq_biggest_place: e.target.value || null })} data-testid="eq-biggest-place" className="col-span-2 w-full flat-border rounded-lg px-3 py-2 outline-none text-sm" placeholder="Lokasi (opsional)" />
              <div className="col-span-3 flex justify-end">
                <button type="button" onClick={()=>{ clear("eq_biggest_mag"); clear("eq_biggest_place"); }} data-testid="eq-biggest-reset" className="flat-btn bg-white rounded-lg px-2 py-1 text-[11px] font-semibold">Reset</button>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Gempa Terbaru</div>
            <div className="grid grid-cols-3 gap-2 items-start">
              <input type="number" step="0.1" min="0" value={local.eq_latest_mag ?? ""} onChange={(e)=>set({ eq_latest_mag: e.target.value === "" ? null : Number(e.target.value) })} data-testid="eq-latest-mag" className="w-full flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" placeholder="mis. 4.2" />
              <input value={local.eq_latest_place || ""} onChange={(e)=>set({ eq_latest_place: e.target.value || null })} data-testid="eq-latest-place" className="col-span-2 w-full flat-border rounded-lg px-3 py-2 outline-none text-sm" placeholder="Lokasi (opsional)" />
              <input type="datetime-local" value={(local.eq_latest_time_iso || "").slice(0,16)} onChange={(e)=>set({ eq_latest_time_iso: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="eq-latest-time" className="col-span-3 w-full flat-border rounded-lg px-3 py-2 outline-none font-mono text-sm" />
              <div className="col-span-3 flex justify-end">
                <button type="button" onClick={()=>{ clear("eq_latest_mag"); clear("eq_latest_place"); clear("eq_latest_time_iso"); }} data-testid="eq-latest-reset" className="flat-btn bg-white rounded-lg px-2 py-1 text-[11px] font-semibold">Reset</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


/* ---------------- Gallery tab ---------------- */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const galleryAbs = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]); // [{file, previewUrl, caption_id, caption_en, location, status}]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [editing, setEditing] = useState({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = async () => {
    setItems((await api.get("/gallery")).data || []);
    try { setUsage((await api.get("/storage/usage")).data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024);
    const skipped = (fileList?.length || 0) - files.length;
    if (skipped > 0) toast.error(`${skipped} file dilewati (bukan gambar / > 5MB)`);
    setQueue((q) => [
      ...q,
      ...files.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f), caption_id: "", caption_en: "", location: "", status: "pending" })),
    ]);
  };

  const updateQueueItem = (idx, patch) => setQueue((q) => q.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeQueueItem = (idx) => setQueue((q) => {
    const it = q[idx];
    if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
    return q.filter((_, i) => i !== idx);
  });
  const clearQueue = () => {
    queue.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    setQueue([]);
    setFileInputKey((k) => k + 1);
  };

  const onUploadAll = async () => {
    if (queue.length === 0) { toast.error("Tidak ada foto"); return; }
    setUploading(true);
    setProgress({ done: 0, total: queue.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < queue.length; i++) {
      updateQueueItem(i, { status: "uploading" });
      try {
        const fd = new FormData();
        fd.append("file", queue[i].file);
        fd.append("caption_id", queue[i].caption_id);
        fd.append("caption_en", queue[i].caption_en);
        fd.append("location", queue[i].location);
        await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        updateQueueItem(i, { status: "done" });
        ok++;
      } catch (e) {
        updateQueueItem(i, { status: "error" });
        fail++;
      }
      setProgress({ done: i + 1, total: queue.length });
    }
    setUploading(false);
    toast[fail === 0 ? "success" : (ok === 0 ? "error" : "message")](`${ok} berhasil${fail ? `, ${fail} gagal` : ""}`);
    if (fail === 0) clearQueue();
    load();
  };

  const saveEdit = async (id) => {
    const patch = editing[id];
    if (!patch) return;
    try {
      await api.put(`/gallery/${id}`, patch);
      toast.success("Tersimpan");
      setEditing((s) => { const n = { ...s }; delete n[id]; return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Terhapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const setEdit = (id, patch) => setEditing((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));

  // Drag-reorder handlers
  const onDragStart = (id) => (ev) => { setDragId(id); ev.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (id) => (ev) => { ev.preventDefault(); if (id !== dragOverId) setDragOverId(id); };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };
  const onDrop = (targetId) => async (ev) => {
    ev.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const src = items.findIndex((x) => x.id === dragId);
    const dst = items.findIndex((x) => x.id === targetId);
    if (src === -1 || dst === -1) { onDragEnd(); return; }
    const next = [...items];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setItems(next); // optimistic
    onDragEnd();
    try {
      await api.put("/gallery/reorder", { order: next.map((x) => x.id) });
      toast.success("Urutan diperbarui");
    } catch (e) {
      toast.error("Gagal simpan urutan");
      load();
    }
  };

  // Drop zone for the multi-file picker
  const [dropHover, setDropHover] = useState(false);
  const onDropZone = (ev) => { ev.preventDefault(); setDropHover(false); addFiles(ev.dataTransfer.files); };

  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Tambah Foto Dokumentasi</h2>
        <p className="text-sm text-[#141414]/60 mb-3">Pilih atau seret beberapa foto sekaligus. Isi caption per foto lalu klik <em>Unggah Semua</em>.</p>

        {usage && (
          <div data-testid="gallery-usage" className={`flat-border rounded-lg px-3 py-2 mb-4 text-xs flex items-center gap-2 flex-wrap ${usage.initialized ? "bg-green-50" : "bg-amber-50"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${usage.initialized ? "bg-green-600" : "bg-amber-500"}`} />
            <span className="font-semibold">
              {usage.initialized ? "Penyimpanan permanen aktif" : "Storage belum aktif"}
            </span>
            <span className="opacity-60">·</span>
            <span>{usage.gallery.count} foto · {usage.gallery.total_bytes < 1024*1024 ? `${(usage.gallery.total_bytes/1024).toFixed(1)} KB` : `${usage.gallery.total_mb} MB`}</span>
            <span className="opacity-60">·</span>
            <span>Batas per foto: {usage.per_file_limit_mb} MB</span>
            <span className="opacity-60">·</span>
            <span className="opacity-70">{usage.note}</span>
          </div>
        )}

        <div
          onDragEnter={(e) => { e.preventDefault(); setDropHover(true); }}
          onDragOver={(e) => { e.preventDefault(); if (!dropHover) setDropHover(true); }}
          onDragLeave={() => setDropHover(false)}
          onDrop={onDropZone}
          data-testid="gallery-dropzone"
          className={`flat-border rounded-xl px-4 py-6 text-center bg-[#fff0d2]/30 transition-colors ${dropHover ? "bg-[#fff0d2] border-[color:var(--maroon)]" : ""}`}
        >
          <input
            key={fileInputKey}
            id="gallery-multi-file"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => addFiles(e.target.files)}
            data-testid="gallery-new-file"
            className="hidden"
          />
          <label htmlFor="gallery-multi-file" className="inline-flex items-center gap-2 cursor-pointer flat-btn-primary rounded-full px-4 py-2 text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Pilih Foto
          </label>
          <div className="text-[11px] text-[#141414]/60 mt-2">atau seret & jatuhkan foto ke sini · maks 5 MB per foto</div>
        </div>

        {queue.length > 0 && (
          <>
            <div className="mt-5 space-y-3">
              {queue.map((it, i) => (
                <div key={i} data-testid={`queue-item-${i}`} className="grid grid-cols-12 gap-3 items-start flat-border rounded-xl p-3 bg-white">
                  <img src={it.previewUrl} alt="" className="col-span-12 sm:col-span-2 w-full h-20 sm:h-16 object-cover rounded-md" />
                  <div className="col-span-12 sm:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input value={it.location} onChange={(e)=>updateQueueItem(i, { location: e.target.value })} data-testid={`queue-loc-${i}`} placeholder="Lokasi / Barang" className="flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                    <div className="text-[10px] font-mono text-[#141414]/60 self-center truncate">
                      {it.file.name} · {(it.file.size / 1024).toFixed(0)} KB
                      {it.status === "uploading" && <span className="ml-2 text-[color:var(--maroon)]">mengunggah…</span>}
                      {it.status === "done" && <span className="ml-2 text-green-700">✓</span>}
                      {it.status === "error" && <span className="ml-2 text-red-700">✕</span>}
                    </div>
                    <input value={it.caption_id} onChange={(e)=>updateQueueItem(i, { caption_id: e.target.value })} data-testid={`queue-cid-${i}`} placeholder="Caption ID" className="flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                    <input value={it.caption_en} onChange={(e)=>updateQueueItem(i, { caption_en: e.target.value })} data-testid={`queue-cen-${i}`} placeholder="Caption EN" className="flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                  </div>
                  <button onClick={()=>removeQueueItem(i)} disabled={uploading} data-testid={`queue-remove-${i}`} className="col-span-12 sm:col-span-1 text-[color:var(--maroon)] hover:text-[#141414] p-1 self-center justify-self-end disabled:opacity-40" aria-label="Hapus dari antrian">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={onUploadAll} disabled={uploading} data-testid="gallery-upload-all-btn" className="flat-btn-primary rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
                <Upload className="w-4 h-4" strokeWidth={2.5} />
                {uploading ? `Mengunggah ${progress.done}/${progress.total}…` : `Unggah Semua (${queue.length})`}
              </button>
              <button onClick={clearQueue} disabled={uploading} data-testid="gallery-clear-queue" className="flat-btn bg-white rounded-full px-4 py-2 font-semibold text-xs disabled:opacity-50">
                Kosongkan
              </button>
              {uploading && (
                <div className="flex-1 min-w-[160px] h-1.5 progress-track">
                  <div className="h-full bg-[color:var(--maroon)] transition-all" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} />
                </div>
              )}
            </div>
          </>
        )}
      </section>

      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display text-xl">Foto Tersimpan ({items.length})</h2>
          {items.length > 1 && <div className="text-[11px] text-[#141414]/60">Seret <GripVertical className="w-3 h-3 inline -mt-0.5" /> untuk mengatur urutan · nomor 1 tampil pertama di beranda</div>}
        </div>
        {items.length === 0 && <div className="text-sm text-[#141414]/60">Belum ada foto.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((g, idx) => {
            const e = editing[g.id];
            const isEditing = !!e;
            const isDragging = dragId === g.id;
            const isOver = dragOverId === g.id && dragId !== g.id;
            return (
              <div
                key={g.id}
                data-testid={`gallery-item-${g.id}`}
                draggable={!isEditing && !uploading}
                onDragStart={onDragStart(g.id)}
                onDragOver={onDragOver(g.id)}
                onDrop={onDrop(g.id)}
                onDragEnd={onDragEnd}
                className={`flat-border rounded-xl overflow-hidden bg-[#fff0d2]/40 transition-all ${isDragging ? "opacity-40" : ""} ${isOver ? "ring-2 ring-[color:var(--maroon)] ring-offset-2 ring-offset-[color:var(--cream)]" : ""}`}
              >
                <div className="relative">
                  <img src={galleryAbs(g.image_url)} alt="" className="w-full h-40 object-cover" draggable={false} />
                  <div data-testid={`gallery-order-${g.id}`} className="absolute top-2 left-2 flat-border bg-white/95 rounded-full px-2 py-0.5 text-[10px] font-bold font-mono">#{idx + 1}</div>
                  {!isEditing && !uploading && (
                    <div className="absolute top-2 right-2 flat-border bg-white/95 rounded-md p-1 cursor-grab active:cursor-grabbing" title="Seret untuk mengatur urutan">
                      <GripVertical className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  {isEditing ? (
                    <>
                      <input value={e.location ?? g.location ?? ""} onChange={(ev)=>setEdit(g.id, { location: ev.target.value })} data-testid={`gallery-edit-loc-${g.id}`} placeholder="Lokasi / Barang" className="w-full flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                      <input value={e.caption_id ?? g.caption_id ?? ""} onChange={(ev)=>setEdit(g.id, { caption_id: ev.target.value })} data-testid={`gallery-edit-cid-${g.id}`} placeholder="Caption ID" className="w-full flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                      <input value={e.caption_en ?? g.caption_en ?? ""} onChange={(ev)=>setEdit(g.id, { caption_en: ev.target.value })} data-testid={`gallery-edit-cen-${g.id}`} placeholder="Caption EN" className="w-full flat-border rounded-md px-2 py-1.5 text-xs outline-none" />
                      <div className="flex gap-2">
                        <button onClick={()=>saveEdit(g.id)} data-testid={`gallery-save-${g.id}`} className="flat-btn-primary rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Simpan</button>
                        <button onClick={()=>setEditing((s)=>{const n={...s};delete n[g.id];return n;})} className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold">Batal</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {g.location && <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#670003]"><MapPin className="w-3 h-3" strokeWidth={2} /> {g.location}</div>}
                      <div className="text-sm font-semibold leading-snug">{g.caption_id || <span className="opacity-40">—</span>}</div>
                      {g.caption_en && <div className="text-xs text-[#141414]/70 italic">{g.caption_en}</div>}
                      <div className="flex gap-2 pt-1">
                        <button onClick={()=>setEditing((s)=>({...s, [g.id]:{}}))} data-testid={`gallery-edit-btn-${g.id}`} className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold">Edit</button>
                        <button onClick={()=>remove(g.id)} data-testid={`gallery-delete-${g.id}`} className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold text-[color:var(--maroon)] flex items-center gap-1"><Trash2 className="w-3 h-3" /> Hapus</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}


/* ---------------- Branding tab ---------------- */
function BrandingTab({ local, set, reloadSettings }) {
  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ logo_url: r.data.logo_url });
      toast.success("Logo diperbarui");
      reloadSettings?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal");
    }
    setUploading(false);
  };
  return (
    <section className="flat-border rounded-2xl bg-white p-6">
      <h2 className="font-display text-xl mb-1">Logo Organisasi</h2>
      <p className="text-sm text-[#141414]/60 mb-5">Unggah gambar (PNG/JPG/SVG, maks 3MB). Logo akan langsung muncul di navigasi.</p>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="flat-border rounded-xl bg-[#fff0d2] p-4 flex items-center justify-center min-w-[160px] min-h-[80px]">
          <img src={local.logo_url} alt="logo" className="max-h-16 max-w-[220px] object-contain" onError={(e)=>{e.target.style.display='none';}} />
        </div>
        <label className="flat-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 cursor-pointer">
          <Upload className="w-4 h-4" strokeWidth={2} />
          {uploading ? "Mengunggah…" : "Unggah Logo"}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="logo-upload" />
        </label>
        <button type="button" onClick={()=>set({ logo_url: "/brand/lepo-gete.png" })} data-testid="logo-reset" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold">Kembali ke Default</button>
      </div>
    </section>
  );
}

/* ---------------- Categories tab ---------------- */
function CategoriesList({ kind, title, subtitle }) {
  const url = kind === "resources" ? "/categories/resources" : "/categories/statuses";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label_id: "", label_en: "" });
  const [reassignFor, setReassignFor] = useState(null); // { key, count }
  const [reassignTo, setReassignTo] = useState("");

  const reload = async () => {
    const r = await api.get(url);
    setItems(r.data);
  };
  useEffect(() => { reload(); }, [url]);

  const add = async () => {
    if (!form.label_id.trim()) { toast.error("Isi nama kategori"); return; }
    try {
      await api.post(url, form);
      setForm({ label_id: "", label_en: "" });
      toast.success("Ditambahkan");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const remove = async (key, reassign) => {
    try {
      await api.delete(`${url}/${key}${reassign ? `?reassign_to=${encodeURIComponent(reassign)}` : ""}`);
      toast.success(reassign ? "Data dipindahkan & kategori dihapus" : "Dihapus");
      setReassignFor(null); setReassignTo("");
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && d.error === "in_use") {
        setReassignFor({ key, count: d.count });
        setReassignTo("");
      } else {
        toast.error((typeof d === "string" ? d : d?.error) || "Gagal");
      }
    }
  };

  const others = items.filter(x => x.key !== reassignFor?.key);

  return (
    <section className="flat-border rounded-2xl bg-white p-6">
      <h2 className="font-display text-xl mb-1">{title}</h2>
      <p className="text-sm text-[#141414]/60 mb-4">{subtitle}</p>
      <div className="space-y-2 mb-4">
        {items.map((c) => (
          <div key={c.key} data-testid={`cat-${kind}-${c.key}`} className="flat-border rounded-lg px-3 py-2 flex items-center gap-3">
            <div className="flex-1">
              <div className="font-semibold">{c.label_id}</div>
              <div className="text-[11px] text-[#141414]/60">{c.label_en} · <span className="font-mono">{c.key}</span></div>
            </div>
            {c.builtin ? (
              <span className="text-[10px] uppercase font-bold text-[#141414]/50 tracking-wider">bawaan</span>
            ) : (
              <button onClick={()=>{ if (window.confirm("Hapus kategori ini?")) remove(c.key); }} data-testid={`cat-del-${c.key}`} className="text-[color:var(--maroon)] hover:text-[color:var(--ink)] p-1"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <TextInput value={form.label_id} onChange={(v)=>setForm({...form, label_id: v})} testid={`cat-${kind}-new-id`} placeholder="Nama (ID)" />
        <TextInput value={form.label_en} onChange={(v)=>setForm({...form, label_en: v})} testid={`cat-${kind}-new-en`} placeholder="Name (EN)" />
        <button onClick={add} data-testid={`cat-${kind}-add`} className="flat-btn-primary rounded-lg px-4 py-2 text-sm font-semibold flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Tambah</button>
      </div>

      {reassignFor && (
        <div className="fixed inset-0 bg-[#141414]/60 z-50 flex items-center justify-center p-4" onClick={()=>{setReassignFor(null); setReassignTo("");}}>
          <div className="bg-white flat-border-2 rounded-2xl p-6 w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
            <div className="font-display text-xl mb-2">Pindahkan sebelum dihapus</div>
            <p className="text-sm text-[#141414]/70 mb-4">
              Kategori <span className="font-mono font-bold">{reassignFor.key}</span> masih dipakai oleh <b>{reassignFor.count}</b> data.
              Pilih kategori tujuan untuk memindahkan data tersebut.
            </p>
            <select value={reassignTo} onChange={(e)=>setReassignTo(e.target.value)} data-testid="reassign-select" className="w-full flat-border rounded-lg px-3 py-2 outline-none bg-white text-sm mb-4">
              <option value="">— Pilih kategori tujuan —</option>
              {others.map((c)=> <option key={c.key} value={c.key}>{c.label_id} ({c.key})</option>)}
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{setReassignFor(null); setReassignTo("");}} className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold">Batal</button>
              <button disabled={!reassignTo} onClick={()=>remove(reassignFor.key, reassignTo)} data-testid="reassign-confirm" className="flat-btn-primary rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50">Pindahkan & Hapus</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
function CategoriesTab() {
  return (
    <div className="space-y-6">
      <CategoriesList kind="resources" title="Kategori Sumber Daya" subtitle="Tambah tipe sumber daya baru (misal: 'Peralatan Medis'). Bawaan tidak dapat dihapus." />
      <CategoriesList kind="statuses" title="Status Warga" subtitle="Tambah status khusus (misal: 'Perlu Ditinjau'). Bawaan tidak dapat dihapus." />
    </div>
  );
}

/* ---------------- Banks tab ---------------- */
function BanksTab() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ bank_name: "", account_number: "", account_holder: "" });
  const [editing, setEditing] = useState(null);

  const reload = async () => setItems((await api.get("/bank-accounts")).data);
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!form.bank_name || !form.account_number || !form.account_holder) { toast.error("Lengkapi semua kolom"); return; }
    try {
      if (editing) await api.put(`/bank-accounts/${editing}`, form);
      else await api.post("/bank-accounts", form);
      setForm({ bank_name: "", account_number: "", account_holder: "" });
      setEditing(null);
      toast.success("Tersimpan");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const edit = (b) => { setForm({ bank_name: b.bank_name, account_number: b.account_number, account_holder: b.account_holder }); setEditing(b.id); };
  const remove = async (id) => {
    if (!window.confirm("Hapus rekening ini?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Dihapus");
    reload();
  };
  const uploadLogo = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bank-accounts/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo bank diperbarui");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload gagal"); }
  };

  return (
    <section className="flat-border rounded-2xl bg-white p-6">
      <h2 className="font-display text-xl mb-1">Rekening Bank</h2>
      <p className="text-sm text-[#141414]/60 mb-4">Kelola daftar rekening yang muncul di &quot;Silahkan transfer ke&quot; pada formulir donasi. Unggah logo bank agar terlihat terpercaya.</p>
      <div className="space-y-2 mb-6">
        {items.length === 0 && <div className="text-sm text-[#141414]/50">Belum ada rekening.</div>}
        {items.map((b) => (
          <div key={b.id} data-testid={`bank-item-${b.id}`} className="flat-border rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap">
            {b.logo_url ? (
              <img src={b.logo_url.startsWith("/api/") ? `${BACKEND}${b.logo_url}` : b.logo_url} alt={b.bank_name} className="w-12 h-12 object-contain flat-border rounded-md bg-[#fff0d2] p-1 shrink-0" onError={(e)=>{e.target.style.display='none';}} />
            ) : (
              <div className="w-12 h-12 flat-border rounded-md bg-[#fff0d2] flex items-center justify-center shrink-0 text-[11px] font-bold">{b.bank_name.slice(0,3).toUpperCase()}</div>
            )}
            <div className="flex-1 min-w-[160px]">
              <div className="font-semibold">{b.bank_name}</div>
              <div className="text-sm">{b.account_number} · {b.account_holder}</div>
            </div>
            <label className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold cursor-pointer flex items-center gap-1">
              <Upload className="w-3 h-3" /> Logo
              <input type="file" accept="image/*" className="hidden" data-testid={`bank-logo-${b.id}`} onChange={(e)=>uploadLogo(b.id, e.target.files?.[0])} />
            </label>
            <button onClick={()=>edit(b)} data-testid={`bank-edit-${b.id}`} className="p-1 hover:text-[color:var(--maroon)]"><Type className="w-4 h-4" /></button>
            <button onClick={()=>remove(b.id)} data-testid={`bank-del-${b.id}`} className="p-1 hover:text-[color:var(--maroon)]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <TextInput value={form.bank_name} onChange={(v)=>setForm({...form, bank_name:v})} testid="bank-name" placeholder="Nama Bank (BCA)" />
        <TextInput value={form.account_number} onChange={(v)=>setForm({...form, account_number:v})} testid="bank-number" placeholder="Nomor Rekening" />
        <TextInput value={form.account_holder} onChange={(v)=>setForm({...form, account_holder:v})} testid="bank-holder" placeholder="Nama Pemilik" />
      </div>
      <div className="flex gap-2">
        <button onClick={submit} data-testid="bank-save" className="flat-btn-primary rounded-full px-5 py-2 text-sm font-semibold">{editing ? "Simpan Perubahan" : "Tambah Rekening"}</button>
        {editing && <button onClick={()=>{setEditing(null); setForm({bank_name:"",account_number:"",account_holder:""});}} className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold">Batal</button>}
      </div>
    </section>
  );
}

/* ---------------- Verify tab ---------------- */
function VerifyTab() {
  const [receivers, setReceivers] = useState([]);
  const [donors, setDonors] = useState([]);

  const reload = async () => {
    const [r, d] = await Promise.all([api.get("/submissions/receiver"), api.get("/submissions/donor")]);
    setReceivers(r.data);
    setDonors(d.data);
  };
  useEffect(() => { reload(); }, []);

  const verifyReceiver = async (id) => {
    try { await api.post(`/submissions/receiver/${id}/verify`); toast.success("Diverifikasi → Warga"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const verifyDonor = async (id) => {
    try { await api.post(`/submissions/donor/${id}/verify`); toast.success("Diverifikasi → Sumber Daya"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const delReceiver = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/submissions/receiver/${id}`); reload(); };
  const delDonor = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/submissions/donor/${id}`); reload(); };

  const statusPill = (s) => {
    const map = { new: "bg-white", converted: "bg-[color:var(--maroon)] text-[color:var(--cream)]", rejected: "bg-[#141414] text-[color:var(--cream)]" };
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flat-border ${map[s] || "bg-white"}`}>{s}</span>;
  };

  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl">Permintaan Bantuan · Saya Butuh Bantuan</h2><span className="text-xs text-[#141414]/60">{receivers.filter(r=>r.status==="new").length} baru</span></div>
        {receivers.length === 0 && <div className="text-sm text-[#141414]/50">Belum ada permintaan.</div>}
        <div className="space-y-2">
          {receivers.map((s) => (
            <div key={s.id} data-testid={`recv-${s.id}`} className="flat-border rounded-lg p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="text-[10px] uppercase font-semibold text-[color:var(--maroon)] tracking-wider">{s.kota}, {s.kabupaten}</div>
                  <div className="font-semibold">{s.pic_name} <span className="text-xs text-[#141414]/60 font-normal">· {s.pic_number}</span></div>
                  <div className="text-sm mt-1">{s.people_count} orang · {s.needs}</div>
                  {s.details && <div className="text-xs text-[#141414]/60 mt-1">{s.details}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {statusPill(s.status)}
                  {s.status !== "converted" && (
                    <button onClick={()=>verifyReceiver(s.id)} data-testid={`recv-verify-${s.id}`} className="flat-btn-primary rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Verifikasi</button>
                  )}
                  <button onClick={()=>delReceiver(s.id)} data-testid={`recv-del-${s.id}`} className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1"><X className="w-3 h-3" /> Hapus</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="flat-border rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl">Donasi · Saya Ingin Donasi</h2><span className="text-xs text-[#141414]/60">{donors.filter(d=>d.status==="new").length} baru</span></div>
        {donors.length === 0 && <div className="text-sm text-[#141414]/50">Belum ada donasi.</div>}
        <div className="space-y-2">
          {donors.map((s) => (
            <div key={s.id} data-testid={`don-${s.id}`} className="flat-border rounded-lg p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="text-[10px] uppercase font-semibold text-[color:var(--maroon)] tracking-wider">{s.donation_type === "money" ? "Uang" : "Barang"}</div>
                  <div className="font-semibold">{s.name} <span className="text-xs text-[#141414]/60 font-normal">· {s.contact}</span></div>
                  <div className="text-sm mt-1">{s.donation_type === "money" ? (s.amount ? (String(s.amount).toLowerCase().startsWith("rp") ? s.amount : `Rp ${s.amount}`) : "—") : s.items}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {statusPill(s.status)}
                  {s.status !== "converted" && (
                    <button onClick={()=>verifyDonor(s.id)} data-testid={`don-verify-${s.id}`} className="flat-btn-primary rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Verifikasi</button>
                  )}
                  <button onClick={()=>delDonor(s.id)} data-testid={`don-del-${s.id}`} className="flat-btn bg-white rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1"><X className="w-3 h-3" /> Hapus</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Export + Import tab ---------------- */
function ExportTab() {
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const exportXls = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0,10).replace(/-/g,"");
      link.download = `lepogete-export-${stamp}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Berhasil diunduh");
    } catch (e) { toast.error("Gagal mengunduh"); }
    setDownloading(false);
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const added = (r.data.warga_added || 0) + (r.data.sumber_daya_added || 0);
      toast.success(`Impor selesai: ${added} baris ditambahkan`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengimpor");
    } finally {
      setImporting(false);
      e.target.value = ""; // reset input
    }
  };

  return (
    <div className="space-y-6">
      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Ekspor Semua Data</h2>
        <p className="text-sm text-[#141414]/60 mb-5">Unduh satu file <span className="font-mono">.xlsx</span> berisi 5 sheet: Warga, Sumber Daya, Donatur, Permintaan, dan Bank.</p>
        <button onClick={exportXls} disabled={downloading} data-testid="export-btn" className="flat-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2">
          <Download className="w-4 h-4" strokeWidth={2} /> {downloading ? "Mengunduh…" : "Unduh .xlsx"}
        </button>
      </section>

      <section className="flat-border rounded-2xl bg-white p-6">
        <h2 className="font-display text-xl mb-1">Impor dari Excel</h2>
        <p className="text-sm text-[#141414]/60 mb-3">
          Unggah file <span className="font-mono">.xlsx</span> untuk menambahkan banyak data sekaligus.
              File dapat berisi sheet <b>Warga</b> dan/atau <b>Sumber Daya</b> — nama kolom harus sama seperti hasil Ekspor.
        </p>
        <details className="mb-4 text-xs text-[#141414]/70">
          <summary className="cursor-pointer font-semibold">Format kolom yang diharapkan</summary>
          <div className="mt-2 flat-border rounded-lg p-3 bg-[#fff0d2] space-y-2">
            <div><b>Warga</b>: <span className="font-mono">kabupaten, kota, pic_name, pic_number, people_count, needs, details, help_provided, status</span><br/>
              <span className="italic">needs</span> bisa berupa daftar dipisah &quot;; &quot; — mis. <span className="font-mono">Beras (10/30 partial); Air (0/5 pending)</span></div>
            <div><b>Sumber Daya</b>: <span className="font-mono">category, name, quantity, contact, location, notes</span> — <span className="italic">category</span> harus cocok dengan salah satu kategori yang ada.</div>
          </div>
        </details>
        <label className="flat-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 cursor-pointer w-fit">
          <Upload className="w-4 h-4" strokeWidth={2} /> {importing ? "Mengunggah…" : "Pilih File .xlsx"}
          <input type="file" accept=".xlsx" className="hidden" onChange={onImport} data-testid="import-input" />
        </label>

        {result && (
          <div className="mt-5 flat-border rounded-lg p-4 bg-[#fff0d2]" data-testid="import-result">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><div className="text-[10px] uppercase font-bold tracking-wider text-[color:var(--maroon)]">Warga</div><div className="text-sm">{result.warga_added} ditambahkan · {result.warga_skipped} dilewati</div></div>
              <div><div className="text-[10px] uppercase font-bold tracking-wider text-[color:var(--maroon)]">Sumber Daya</div><div className="text-sm">{result.sumber_daya_added} ditambahkan · {result.sumber_daya_skipped} dilewati</div></div>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-[color:var(--maroon)] mb-1">Baris gagal ({result.errors.length})</div>
                <div className="max-h-40 overflow-y-auto text-xs font-mono space-y-1">
                  {result.errors.slice(0, 20).map((er, i) => (
                    <div key={i}>{er.sheet} baris {er.row}: {er.error}</div>
                  ))}
                  {result.errors.length > 20 && <div>… dan {result.errors.length - 20} lainnya</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------------- Main page ---------------- */
export default function AdminSettings() {
  const { user, settings, applySettings, resetSettings, checking } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("theme");
  const [local, setLocal] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(settings);

  useEffect(() => { setLocal(settings); setLastSaved(settings); }, [JSON.stringify(settings)]); // eslint-disable-line
  useEffect(() => { if (!checking && !user) navigate("/login"); }, [user, checking, navigate]);

  const set = (patch) => setLocal((l) => ({ ...l, ...patch }));

  useEffect(() => {
    const diff = Object.keys(local).some((k) => local[k] !== lastSaved[k]);
    if (!diff) { setSaving(false); return; }
    setSaving(true);
    const h = setTimeout(async () => {
      const patch = {};
      for (const k of Object.keys(local)) if (local[k] !== lastSaved[k]) patch[k] = local[k];
      const r = await applySettings(patch);
      setSaving(false);
      if (r.ok) {
        setLastSaved(r.settings);
        toast.success("Tersimpan otomatis", { duration: 1200 });
      } else {
        toast.error(`Ditolak: ${r.error}. Dikembalikan.`);
        setLocal(lastSaved);
      }
    }, 500);
    return () => clearTimeout(h);
  }, [local]); // eslint-disable-line

  const doReset = async () => {
    if (!window.confirm("Reset semua tampilan ke default?")) return;
    const r = await resetSettings();
    if (r.ok) toast.success("Direset ke default"); else toast.error(r.error || "Gagal reset");
  };
  const doRevert = () => { setLocal(lastSaved); toast.success("Kembali ke terakhir tersimpan"); };

  if (checking) return <div className="max-w-4xl mx-auto p-10 text-[#141414]/50">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold text-[#141414]/60 hover:text-[#141414] mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Beranda
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
        <div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight">Panel Admin</h1>
          <p className="mt-2 text-[#141414]/70 max-w-xl text-sm">Kelola tampilan, konten, kategori, bank, verifikasi formulir, dan ekspor data.</p>
        </div>
        {tab === "theme" && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={doRevert} data-testid="revert-btn" className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2"><Undo2 className="w-4 h-4" /> Batalkan</button>
            <button onClick={doReset} data-testid="reset-btn" className="flat-btn-primary rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Reset</button>
          </div>
        )}
      </div>

      {/* Tabs — two rows: Konten Publik / Operasi */}
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 no-scrollbar">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#141414]/50 shrink-0 mr-1 hidden sm:inline">Konten Publik</span>
          {TABS_PUBLIC.map((tt) => (
            <button key={tt.id} data-testid={`tab-${tt.id}`} onClick={() => setTab(tt.id)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                tab === tt.id ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "bg-transparent border-[color:var(--ink)]"
              }`}>
              <tt.icon className="w-3.5 h-3.5" strokeWidth={2} /> {tt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 no-scrollbar">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#670003] shrink-0 mr-1 hidden sm:inline">Operasi · Internal</span>
          {TABS_OPS.map((tt) => (
            <button key={tt.id} data-testid={`tab-${tt.id}`} onClick={() => setTab(tt.id)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                tab === tt.id ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "bg-transparent border-[color:var(--ink)]"
              }`}>
              <tt.icon className="w-3.5 h-3.5" strokeWidth={2} /> {tt.label}
            </button>
          ))}
        </div>
      </div>

      {(tab === "theme" || tab === "content" || tab === "earthquake" || tab === "branding") && (
        <div className="inline-flex items-center gap-2 flat-border rounded-full px-3 py-1 mb-6 text-[11px] font-semibold">
          <span className={`w-1.5 h-1.5 rounded-full ${saving ? "bg-amber-500 animate-pulse" : "bg-[color:var(--maroon)]"}`} />
          {saving ? "Menyimpan…" : "Tersimpan"}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={((tab === "theme" || tab === "content" || tab === "earthquake" || tab === "branding") ? "lg:col-span-2" : "lg:col-span-3")}>
          {tab === "theme" && <ThemeTab local={local} set={set} />}
          {tab === "content" && <ContentTab local={local} set={set} />}
          {tab === "earthquake" && <EarthquakeTab local={local} set={set} />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "branding" && <BrandingTab local={local} set={set} />}
          {tab === "categories" && <CategoriesTab />}
          {tab === "banks" && <BanksTab />}
          {tab === "verify" && <VerifyTab />}
          {tab === "cashflow" && <CashflowTab />}
          {tab === "inventory" && <InventoryTab />}
          {tab === "export" && <ExportTab />}
        </div>

        {(tab === "theme" || tab === "content" || tab === "earthquake" || tab === "branding") && (
          <aside className="lg:sticky lg:top-24 h-fit">
            <section className="flat-border rounded-2xl bg-white p-6">
              <div className="flex items-center gap-2 mb-4"><Settings2 className="w-4 h-4" /><div className="font-display text-xl">Pratinjau</div></div>
              <div className="flat-border rounded-2xl p-5 mb-3" style={{ backgroundColor: local.color_background, color: local.color_ink }}>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: local.color_primary }}>{local.hero_kicker_id}</div>
                <div className="font-display mt-2 leading-tight" style={{ fontSize: `${1.9 * local.heading_scale}rem` }}>{local.hero_title_id}</div>
                <p className="text-sm mt-2 opacity-80">{local.hero_subtitle_id}</p>
                <button className="mt-3 rounded-full px-4 py-2 text-sm font-semibold" style={{ background: local.color_primary, color: local.color_background, borderWidth: local.border_width, borderColor: local.color_ink, borderStyle: "solid" }}>Bagikan</button>
              </div>
              <p className="text-xs text-[#141414]/60 leading-relaxed">Perubahan tersimpan otomatis dan langsung diterapkan. Jika error, pengaturan dikembalikan otomatis.</p>
            </section>
          </aside>
        )}
      </div>
    </div>
  );
}
